import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import React from 'react';
import {
  LayoutChangeEvent,
  StyleProp,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Button } from './Button';
import { Icon, ICONS } from './Icon';
import { ICON_BUTTON_HEIGHT, IconButton, IconButtonProps } from './IconButton';
import { Menu, MenuProps } from './Menu';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
import { useSelector } from 'hooks/useSelector';
import {
  selectSidebar,
  selectAppType,
  selectOfflineOnly,
} from 'store/app/selectors';
import { navigationRef } from './Navigation/navigationRef';
import { DrawerActions } from '@react-navigation/native';
import { IsInHeaderProvider } from 'hooks/isInHeader';
import { useIsInModal } from 'hooks/isInModal';
import { getPageHeaderPaddingTop } from 'lib/riskbase';
import { ButtonGroup } from './ButtonGroup';
import { useIsInHome } from 'hooks/isInHome';

export const NAVIGATION_HEADER_HEIGHT = 73;

export type PageHeaderProps = {
  onPressBackButton?: (() => void) | false;
  onPressCloseButton?: (() => void) | false;
  onPressCancelButton?: (() => void) | false;
  onPressSaveButton?: (() => void) | null;
  onPressSubtitle?: () => void;
  title?: string;
  /**
   * Appears after the title in a secondary colour
   */
  secondaryTitle?: string;
  subtitle?: string | false;
  subtitleIcon?: ICONS;
  buttons?: React.ReactNode;
  buttonsLeft?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  iconButtonLeft?: IconButtonProps;
  iconButtonRight?: IconButtonProps;
  menuRight?: MenuProps | false;
  saveButtonText?: string;
  saveButtonIcon?: ICONS;
  closeIcon?: ICONS;
  buttonsLeftStyle?: StyleProp<ViewStyle>;
  backButtonText?: string;
  backButtonMaxWidth?: number;
  children?: React.ReactNode;
  onLayout?: ViewProps['onLayout'];
  //
  alignContent?: 'center' | 'left';
  showSidebarButton?: boolean;
  showSidebarButtonDisabled?: boolean;
  gap?: number;
  ignoreSafeArea?: boolean;
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  buttons,
  buttonsLeft,
  buttonsLeftStyle,
  // cancelButtonText,
  backButtonText,
  backButtonMaxWidth,
  children,
  closeIcon,
  style,
  contentStyle,
  title,
  secondaryTitle,
  iconButtonLeft,
  iconButtonRight,
  menuRight,
  onPressBackButton: incomingOnPressBackButton,
  onPressCancelButton,
  onPressCloseButton,
  onPressSaveButton,
  saveButtonText,
  saveButtonIcon,
  subtitle,
  subtitleIcon,
  onPressSubtitle,
  onLayout,
  //
  // alignContent: incomingAlignContent = 'center',
  showSidebarButton,
  showSidebarButtonDisabled,
  gap = theme.padding / 2,
  ignoreSafeArea = false,
}) => {
  const sidebar = useSelector(selectSidebar);
  const appType = useSelector(selectAppType);
  const offlineOnly = useSelector(selectOfflineOnly);

  const safeAreaInsets = useSafeAreaInsets();
  const forceUpdate = useForceUpdate();
  const screenSize = useScreenSize();
  const isInModal = useIsInModal();
  const isInHome = useIsInHome();

  // Maintain a centered title, even with different left and right items
  const leftWidthRef = React.useRef(0);
  const rightWidthRef = React.useRef(0);
  const maxSideWidth = React.useRef<number | undefined>(undefined);
  const alignContent = 'center';
  // screenSize === ScreenSize.small ? 'center' : incomingAlignContent;

  const handleLayoutLeftRight = React.useCallback(() => {
    const newWidth = Math.max(
      leftWidthRef.current || 0,
      rightWidthRef.current || 0,
    );
    if (alignContent === 'center' && newWidth !== maxSideWidth.current) {
      maxSideWidth.current = newWidth;
      forceUpdate();
    }
  }, [alignContent, forceUpdate]);

  const handleLayoutLeft = React.useCallback(
    (event: LayoutChangeEvent) => {
      leftWidthRef.current = Math.round(event.nativeEvent.layout.width);
      handleLayoutLeftRight();
    },
    [handleLayoutLeftRight],
  );
  const handleLayoutRight = React.useCallback(
    (event: LayoutChangeEvent) => {
      rightWidthRef.current = Math.round(event.nativeEvent.layout.width);
      handleLayoutLeftRight();
    },
    [handleLayoutLeftRight],
  );

  const onPressBackButton =
    incomingOnPressBackButton !== undefined
      ? incomingOnPressBackButton
      : // Show back button if: is mobile, with more than offline, is in home
        showSidebarButton && isInHome && appType === 'mobile' && !offlineOnly
        ? navigationRef.current?.goBack
        : undefined;

  const finalShowSidebarButton =
    showSidebarButton && (!sidebar.isPinned || !sidebar.canBePinned);

  const finalShowSidebarButtonDisabled =
    !finalShowSidebarButton &&
    showSidebarButtonDisabled &&
    (!sidebar.isPinned || !sidebar.canBePinned);

  const hasLeftContent =
    // alignContent === 'center' ||
    !!finalShowSidebarButtonDisabled ||
    !!finalShowSidebarButton ||
    !!onPressCancelButton ||
    !!onPressCloseButton ||
    !!onPressBackButton ||
    !!iconButtonLeft ||
    !!buttonsLeft;

  const hasRightContent =
    !!iconButtonRight || !!menuRight || !!onPressSaveButton || !!buttons;

  const { paddingTopNormal, paddingTop } = getPageHeaderPaddingTop(
    isInModal,
    !isInModal && !ignoreSafeArea ? safeAreaInsets.top : 0,
  );

  return (
    <IsInHeaderProvider value={true}>
      <View
        style={[
          {
            paddingLeft: 16 + safeAreaInsets.left,
            paddingRight: 16 + safeAreaInsets.right,
            paddingTop: paddingTop,
            paddingBottom: 13,
            minHeight:
              NAVIGATION_HEADER_HEIGHT - (paddingTopNormal - paddingTop),
            alignItems: 'center',
            flexDirection: 'row',
            gap,
          },
          style,
        ]}
        onLayout={onLayout}
      >
        {/** Left Aligned */}
        {(hasRightContent || hasLeftContent) && (
          <View
            style={{
              zIndex: 1,
              alignItems: 'flex-start',
              width:
                alignContent === 'center' ? maxSideWidth.current : undefined,
            }}
          >
            <View
              style={{
                alignItems: 'center',
                flexDirection: 'row',
                gap: theme.paddingHorizontalHalf,
              }}
              onLayout={handleLayoutLeft}
            >
              {finalShowSidebarButtonDisabled && (
                <View
                  style={{
                    width: ICON_BUTTON_HEIGHT,
                    height: ICON_BUTTON_HEIGHT,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    source={ICONS.RBPlectrumSolidCutout}
                    size={24}
                    style={{ marginTop: 2 }}
                    color={theme.colors.green}
                  />
                </View>
              )}
              {finalShowSidebarButton && (
                <IconButton
                  icon={ICONS.RBPlectrumSolid}
                  iconSize={24}
                  color={theme.colors.green}
                  imageStyle={{ marginTop: 2 }}
                  onPress={() =>
                    navigationRef.current?.dispatch(DrawerActions.openDrawer())
                  }
                />
              )}
              {onPressCancelButton && (
                <IconButton
                  icon={ICONS.XmarkLight}
                  onPress={onPressCancelButton}
                />
              )}
              {onPressCloseButton && (
                <IconButton
                  icon={closeIcon || ICONS.XmarkLight}
                  onPress={onPressCloseButton}
                />
              )}
              {onPressBackButton &&
                (!!backButtonText && screenSize !== ScreenSize.small ? (
                  <Button
                    icon={ICONS.ChevronLeftLight}
                    text={backButtonText}
                    onPress={onPressBackButton}
                    style={{
                      maxWidth: backButtonMaxWidth,
                    }}
                  />
                ) : (
                  <IconButton
                    imageStyle={{
                      transform: [{ rotate: '-180deg' }],
                      marginLeft: -3,
                    }}
                    icon={ICONS.ChevronRightLight}
                    onPress={onPressBackButton}
                  />
                ))}
              {iconButtonLeft && (
                <View>
                  <IconButton
                    iconSize={iconButtonLeft.iconSize}
                    color={iconButtonLeft.color}
                    disabled={iconButtonLeft.disabled}
                    imageStyle={iconButtonLeft.imageStyle}
                    icon={iconButtonLeft.icon}
                    marginHorizontal={iconButtonLeft.marginHorizontal}
                    onPress={iconButtonLeft.onPress}
                    onPressIn={iconButtonLeft.onPressIn}
                    size={iconButtonLeft.size}
                    style={iconButtonLeft.style}
                    showBadge={iconButtonLeft.showBadge}
                  />
                </View>
              )}
              {!!buttonsLeft && (
                <View
                  style={[
                    {
                      alignItems: 'center',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                    },
                    buttonsLeftStyle,
                  ]}
                >
                  {buttonsLeft}
                </View>
              )}
            </View>
          </View>
        )}

        {/** Content: Left or Center Aligned */}
        <View
          style={[
            { flex: 1 },
            // !hasLeftContent && { marginLeft: theme.paddingHorizontalHalf },
            contentStyle,
          ]}
        >
          {(!!title || !!subtitle) && (
            <View
              style={{
                flexDirection: 'column',
                alignItems: 'center',
                gap: !!subtitle && onPressSubtitle ? 0 : theme.padding / 3,
              }}
            >
              {!!title && (
                <Txt size="normal" weight="bold" numberOfLines={1}>
                  {title}
                  {!!secondaryTitle && (
                    <Txt
                      size="normal"
                      weight="regular"
                      numberOfLines={1}
                      style={{ color: theme.colors.fieldValueText }}
                    >
                      {' '}
                      {secondaryTitle}
                    </Txt>
                  )}
                </Txt>
              )}
              {!!subtitle &&
                (onPressSubtitle ? (
                  <IsInHeaderProvider value={false}>
                    <Button
                      icon={subtitleIcon}
                      iconSize={15}
                      onPress={onPressSubtitle}
                      text={subtitle}
                      textSize="small"
                      textWeight="regular"
                      style={{
                        marginTop: -1,
                        height: 18,
                      }}
                    />
                  </IsInHeaderProvider>
                ) : (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      flex: 1,
                    }}
                  >
                    {!!subtitleIcon && (
                      <Icon
                        source={subtitleIcon}
                        size={15}
                        color={theme.colors.fieldValueText}
                        style={{
                          marginTop: -5,
                          marginRight: theme.padding / 2,
                        }}
                      />
                    )}
                    <Txt
                      size="small"
                      numberOfLines={1}
                      style={{
                        marginTop: -5,
                        color: theme.colors.fieldValueText,
                      }}
                    >
                      {subtitle}
                    </Txt>
                  </View>
                ))}
            </View>
          )}
          {children}
        </View>

        {/** Right Aligned */}
        {(hasRightContent || hasLeftContent) && (
          <View
            style={{
              zIndex: 1,
              alignItems: 'flex-end',
              width:
                alignContent === 'center' ? maxSideWidth.current : undefined,
            }}
          >
            <View
              style={{
                alignItems: 'center',
                flexDirection: 'row',
                // gap: theme.paddingHorizontalHalf,
              }}
              onLayout={handleLayoutRight}
            >
              <ButtonGroup>
                {buttons}
                {iconButtonRight && (
                  <IconButton
                    iconSize={iconButtonRight.iconSize}
                    color={iconButtonRight.color}
                    disabled={iconButtonRight.disabled}
                    imageStyle={iconButtonRight.imageStyle}
                    icon={iconButtonRight.icon}
                    marginHorizontal={iconButtonRight.marginHorizontal}
                    onPress={iconButtonRight.onPress}
                    onPressIn={iconButtonRight.onPressIn}
                    size={iconButtonRight.size}
                    style={iconButtonRight.style}
                    showBadge={iconButtonRight.showBadge}
                  />
                )}
                {menuRight && <Menu {...menuRight} />}
                {onPressSaveButton !== undefined &&
                  (!!saveButtonText ? (
                    <Button
                      text={saveButtonText}
                      icon={saveButtonIcon}
                      onPress={onPressSaveButton || undefined}
                    />
                  ) : (
                    <IconButton
                      icon={saveButtonIcon || ICONS.CheckLight}
                      onPress={onPressSaveButton || undefined}
                    />
                  ))}
              </ButtonGroup>
            </View>
          </View>
        )}
      </View>
    </IsInHeaderProvider>
  );
};
